/**
 * Doc lifecycle visuals — ERPNext-style colored indicator + label (plan/30).
 * Posted uses primary (not ok green) so it stays distinct from fulfillment ✓.
 */

/** Doc lifecycle values used in Desk lists — plan/30. */
export type LifecycleKind =
  | "draft"
  | "open"
  | "posted"
  | "parked"
  | "void";

export type LifecycleVisual = {
  kind: LifecycleKind;
  label: string;
  /** Tailwind class for the indicator dot fill */
  dotClassName: string;
};

/** Editable / not submitted yet. */
const DRAFT: LifecycleVisual = {
  kind: "draft",
  label: "Draft",
  dotClassName: "bg-[var(--color-warn)]",
};

/**
 * SO/PO wire value is `open` (= posted/confirmed — plan/30).
 * Show as Posted so it matches payments/POS and isn’t confused with Draft.
 */
const OPEN: LifecycleVisual = {
  kind: "open",
  label: "Posted",
  dotClassName: "bg-[var(--color-primary)]",
};

const POSTED: LifecycleVisual = {
  kind: "posted",
  label: "Posted",
  dotClassName: "bg-[var(--color-primary)]",
};

const PARKED: LifecycleVisual = {
  kind: "parked",
  label: "Parked",
  dotClassName: "bg-[var(--color-muted-fg)]",
};

const VOID: LifecycleVisual = {
  kind: "void",
  label: "Void",
  dotClassName: "bg-[var(--color-destructive)]",
};

const BY_KIND: Record<LifecycleKind, LifecycleVisual> = {
  draft: DRAFT,
  open: OPEN,
  posted: POSTED,
  parked: PARKED,
  void: VOID,
};

/** SO/PO lists: draft · open(=Posted) · void */
export const ORDER_LIFECYCLE_LEGEND: LifecycleKind[] = [
  "draft",
  "open",
  "void",
];

/** Payments / returns / GR: draft · posted · void */
export const POSTED_DOC_LIFECYCLE_LEGEND: LifecycleKind[] = [
  "draft",
  "posted",
  "void",
];

/** POS tickets: draft · parked · posted · void */
export const POS_LIFECYCLE_LEGEND: LifecycleKind[] = [
  "draft",
  "parked",
  "posted",
  "void",
];

export function lifecycleVisual(
  raw: string | null | undefined,
): LifecycleVisual | null {
  if (raw == null || raw === "") return null;
  const key = raw.trim().toLowerCase();
  if (key in BY_KIND) return BY_KIND[key as LifecycleKind];
  return null;
}

export function lifecycleLegendItems(
  kinds: LifecycleKind[],
): LifecycleVisual[] {
  return kinds.map((k) => BY_KIND[k]);
}
